import { useState } from "react";
import { useAuth } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Eye, FileText, User, Briefcase, Users, CheckCircle, ArrowLeft, ArrowRight, Camera, Upload } from "lucide-react";

const MARITAL_STATUSES = ["Married", "Single", "Widowed", "Divorced", "Other"];

export default function AdminApplicationFormView() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Info
    full_name: "",
    age: "",
    gender: "",
    mobile_phone: "",
    whatsapp_phone: "",
    marital_status: "",
    national_id: "",
    driver_license: "",
    // Residency
    last_address_1: "",
    last_address_2: "",
    reason_leaving_1: "",
    reason_leaving_2: "",
    landlord_1: "",
    landlord_phone_1: "",
    landlord_2: "",
    landlord_phone_2: "",
    // Employment
    occupation: "",
    employer: "",
    employer_address: "",
    employer_phone: "",
    // Guarantors
    guarantor1_name: "",
    guarantor1_phone: "",
    guarantor1_address: "",
    guarantor1_occupation: "",
    guarantor2_name: "",
    guarantor2_phone: "",
    guarantor2_address: "",
    guarantor2_occupation: "",
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      full_name: "",
      age: "",
      gender: "",
      mobile_phone: "",
      whatsapp_phone: "",
      marital_status: "",
      national_id: "",
      driver_license: "",
      last_address_1: "",
      last_address_2: "",
      reason_leaving_1: "",
      reason_leaving_2: "",
      landlord_1: "",
      landlord_phone_1: "",
      landlord_2: "",
      landlord_phone_2: "",
      occupation: "",
      employer: "",
      employer_address: "",
      employer_phone: "",
      guarantor1_name: "",
      guarantor1_phone: "",
      guarantor1_address: "",
      guarantor1_occupation: "",
      guarantor2_name: "",
      guarantor2_phone: "",
      guarantor2_address: "",
      guarantor2_occupation: "",
    });
  };

  const steps = [
    { number: 1, title: "Personal Info", icon: User },
    { number: 2, title: "Residency", icon: User },
    { number: 3, title: "Employment", icon: Briefcase },
    { number: 4, title: "Guarantors", icon: Users },
    { number: 5, title: "Review", icon: CheckCircle },
  ];

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-application-form-view-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary">Form View</h1>
                <p className="text-text-muted">Preview the New Tenant Application form</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Badge className="bg-blue-100 text-blue-800">
                <Eye className="w-3 h-3 mr-1" />
                Preview Mode
              </Badge>
              <Badge className="bg-stone-100 text-stone-600">
                Admin Only
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={resetForm}
                className="ml-auto"
              >
                Reset Form
              </Button>
            </div>
          </div>

          {/* Info Banner */}
          <Card className="border-blue-200 bg-blue-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800">
                    This is a preview of the <strong>New Tenant Application</strong> form (5-step wizard). 
                    This form is used during new user registration and by existing users applying for tenancy.
                    You can navigate through all steps to see the complete form structure.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Preview */}
          <Card className="border-stone-200">
            <CardHeader className="border-b border-stone-100">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                New Tenant Application Form
              </CardTitle>
              <CardDescription>
                This is exactly what applicants see when applying for tenancy
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center">
                    <button
                      onClick={() => setCurrentStep(step.number)}
                      className={`flex flex-col items-center cursor-pointer ${
                        currentStep === step.number ? 'text-primary' : 
                        currentStep > step.number ? 'text-green-600' : 'text-stone-400'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                        currentStep === step.number ? 'bg-primary text-white' :
                        currentStep > step.number ? 'bg-green-600 text-white' : 'bg-stone-200'
                      }`}>
                        {currentStep > step.number ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <step.icon className="w-5 h-5" />
                        )}
                      </div>
                      <span className="text-xs font-medium whitespace-nowrap">{step.title}</span>
                    </button>
                    {index < steps.length - 1 && (
                      <div className={`w-12 md:w-24 h-1 mx-2 ${
                        currentStep > step.number ? 'bg-green-600' : 'bg-stone-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>

              {/* Step 1: Personal Info */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-primary">Step 1: Personal Information</h3>
                  
                  {/* Photo Upload Placeholder */}
                  <div className="flex justify-center mb-6">
                    <div className="w-32 h-32 rounded-full bg-stone-100 border-2 border-dashed border-stone-300 flex flex-col items-center justify-center">
                      <Camera className="w-8 h-8 text-stone-400 mb-2" />
                      <span className="text-xs text-stone-500">Upload Photo</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        placeholder="Enter your full name"
                        value={formData.full_name}
                        onChange={(e) => handleChange("full_name", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Age *</Label>
                      <Input
                        type="number"
                        placeholder="Enter your age"
                        value={formData.age}
                        onChange={(e) => handleChange("age", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Gender *</Label>
                      <Select value={formData.gender} onValueChange={(v) => handleChange("gender", v)}>
                        <SelectTrigger className="h-12 bg-stone-50">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Marital Status *</Label>
                      <Select value={formData.marital_status} onValueChange={(v) => handleChange("marital_status", v)}>
                        <SelectTrigger className="h-12 bg-stone-50">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {MARITAL_STATUSES.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <fieldset className="border border-stone-200 rounded-xl p-4">
                    <legend className="px-2 text-sm font-medium text-primary">Contact Info (at least one required)</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Mobile Phone #</Label>
                        <Input
                          placeholder="e.g., +234 801 234 5678"
                          value={formData.mobile_phone}
                          onChange={(e) => handleChange("mobile_phone", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>WhatsApp # (preferred)</Label>
                        <Input
                          placeholder="e.g., +234 801 234 5678"
                          value={formData.whatsapp_phone}
                          onChange={(e) => handleChange("whatsapp_phone", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                    </div>
                  </fieldset>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>National ID Number *</Label>
                      <Input
                        placeholder="Enter national ID"
                        value={formData.national_id}
                        onChange={(e) => handleChange("national_id", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Driver's License (optional)</Label>
                      <Input
                        placeholder="Enter driver's license"
                        value={formData.driver_license}
                        onChange={(e) => handleChange("driver_license", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Residency */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-primary">Step 2: Residency History</h3>
                  
                  <fieldset className="border border-stone-200 rounded-xl p-4">
                    <legend className="px-2 text-sm font-medium text-primary">Previous Address #1</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Address *</Label>
                        <Input
                          placeholder="Enter previous address"
                          value={formData.last_address_1}
                          onChange={(e) => handleChange("last_address_1", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Reason for Leaving</Label>
                        <Input
                          placeholder="Why did you leave?"
                          value={formData.reason_leaving_1}
                          onChange={(e) => handleChange("reason_leaving_1", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Landlord Name</Label>
                        <Input
                          placeholder="Landlord's name"
                          value={formData.landlord_1}
                          onChange={(e) => handleChange("landlord_1", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Landlord Phone</Label>
                        <Input
                          placeholder="Landlord's phone"
                          value={formData.landlord_phone_1}
                          onChange={(e) => handleChange("landlord_phone_1", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                    </div>
                  </fieldset>

                  <fieldset className="border border-stone-200 rounded-xl p-4">
                    <legend className="px-2 text-sm font-medium text-primary">Previous Address #2 (optional)</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Address</Label>
                        <Input
                          placeholder="Enter previous address"
                          value={formData.last_address_2}
                          onChange={(e) => handleChange("last_address_2", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Landlord Name</Label>
                        <Input
                          placeholder="Landlord's name"
                          value={formData.landlord_2}
                          onChange={(e) => handleChange("landlord_2", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Landlord Phone</Label>
                        <Input
                          placeholder="Landlord's phone"
                          value={formData.landlord_phone_2}
                          onChange={(e) => handleChange("landlord_phone_2", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>
              )}

              {/* Step 3: Employment */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-primary">Step 3: Employment Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Occupation *</Label>
                      <Input
                        placeholder="Your occupation"
                        value={formData.occupation}
                        onChange={(e) => handleChange("occupation", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Employer Name</Label>
                      <Input
                        placeholder="Company/employer name"
                        value={formData.employer}
                        onChange={(e) => handleChange("employer", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Employer Address</Label>
                      <Input
                        placeholder="Employer's address"
                        value={formData.employer_address}
                        onChange={(e) => handleChange("employer_address", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Employer Phone</Label>
                      <Input
                        placeholder="Employer's phone"
                        value={formData.employer_phone}
                        onChange={(e) => handleChange("employer_phone", e.target.value)}
                        className="h-12 bg-stone-50"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Guarantors */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-primary">Step 4: Guarantor Information</h3>
                  
                  <fieldset className="border border-stone-200 rounded-xl p-4">
                    <legend className="px-2 text-sm font-medium text-primary">Guarantor #1 *</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          placeholder="Guarantor's name"
                          value={formData.guarantor1_name}
                          onChange={(e) => handleChange("guarantor1_name", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone Number *</Label>
                        <Input
                          placeholder="Guarantor's phone"
                          value={formData.guarantor1_phone}
                          onChange={(e) => handleChange("guarantor1_phone", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Address *</Label>
                        <Input
                          placeholder="Guarantor's address"
                          value={formData.guarantor1_address}
                          onChange={(e) => handleChange("guarantor1_address", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Occupation</Label>
                        <Input
                          placeholder="Guarantor's occupation"
                          value={formData.guarantor1_occupation}
                          onChange={(e) => handleChange("guarantor1_occupation", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                    </div>
                  </fieldset>

                  <fieldset className="border border-stone-200 rounded-xl p-4">
                    <legend className="px-2 text-sm font-medium text-primary">Guarantor #2 *</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          placeholder="Guarantor's name"
                          value={formData.guarantor2_name}
                          onChange={(e) => handleChange("guarantor2_name", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone Number *</Label>
                        <Input
                          placeholder="Guarantor's phone"
                          value={formData.guarantor2_phone}
                          onChange={(e) => handleChange("guarantor2_phone", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Address *</Label>
                        <Input
                          placeholder="Guarantor's address"
                          value={formData.guarantor2_address}
                          onChange={(e) => handleChange("guarantor2_address", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Occupation</Label>
                        <Input
                          placeholder="Guarantor's occupation"
                          value={formData.guarantor2_occupation}
                          onChange={(e) => handleChange("guarantor2_occupation", e.target.value)}
                          className="h-12 bg-stone-50"
                        />
                      </div>
                    </div>
                  </fieldset>
                </div>
              )}

              {/* Step 5: Review */}
              {currentStep === 5 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-primary">Step 5: Review Your Application</h3>
                  <p className="text-text-muted">This step shows a summary of all information entered. The applicant can review before submitting.</p>
                  
                  <div className="bg-stone-50 rounded-xl p-4 space-y-4">
                    <div>
                      <h4 className="font-semibold text-primary">Personal Information</h4>
                      <p className="text-sm text-stone-600">Name, age, gender, contact details, ID numbers...</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Residency History</h4>
                      <p className="text-sm text-stone-600">Previous addresses, landlord references...</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Employment</h4>
                      <p className="text-sm text-stone-600">Occupation, employer details...</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary">Guarantors</h4>
                      <p className="text-sm text-stone-600">Two guarantor references with contact details...</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6 border-t border-stone-200 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  className="rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                
                {currentStep < 5 ? (
                  <Button
                    onClick={() => setCurrentStep(Math.min(5, currentStep + 1))}
                    className="bg-primary hover:bg-primary-800 rounded-full"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="bg-stone-400 cursor-not-allowed rounded-full"
                  >
                    Submit (Disabled in Preview)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
